import React, { FC, useState } from 'react';
import buttons from 'style/buttons.module.scss';
import Title from 'components/common/Title';
import { useSelector } from 'react-redux';
import { selectMonthlyPlan, selectYearlyPlan } from 'selectors/payments';
import { AvailablePlan } from 'reducers/payments';
import styles from './index.module.scss';
import { UserSubscriptionStatus } from '../../types';
import { isSubscriptionCancelable } from '../../utils/helpers/payments';

interface Props {
  selectPlan: (plan: AvailablePlan) => void;
  subscriptionPlanId: string;
  subscriptionStatus: UserSubscriptionStatus;
  openCancelPage: () => void;
}
const BillingPlans: FC<Props> = ({
  selectPlan,
  subscriptionPlanId,
  subscriptionStatus,
  openCancelPage,
}) => {
  const [visible, setVisible] = useState(false);
  const monthlyPlan = useSelector(selectMonthlyPlan) as AvailablePlan;
  const yearlyPlan = useSelector(selectYearlyPlan) as AvailablePlan;

  const isMonthlyPlanActive = monthlyPlan?.id === subscriptionPlanId;
  const isYearlyPlanActive = yearlyPlan?.id === subscriptionPlanId;

  const selectPlanHandler = (targetPlan: AvailablePlan) => () =>
    selectPlan(targetPlan);

  const getSubscribeButtonText = (isActive: boolean, text: string) => {
    if (!isActive) {
      return `Subscribe ${text}`;
    }

    switch (subscriptionStatus) {
      case UserSubscriptionStatus.CANCELING:
        return `${text} (Canceling)`;

      case UserSubscriptionStatus.UNPAID:
        return `${text} (Unpaid)`;

      default:
        return `${text} (Active)`;
    }
  };

  return (
    <>
      <div className={styles.billingPlans}>
        <div className={`${styles.plan} ${styles.mainBlock}`}>
          <Title>Month to Month Plan</Title>
          <span>$6.99 per Month</span>
          <p>($6.99 billed monthly)</p>
          <ul>
            <li>Up to 5 scholar profiles</li>
            <li>Unlimited Access to Aneta Map themes and Anetapack library</li>
            <li>Change or cancel your plan anytime!</li>
            <li>Peace of mind for about 25 cents a day!</li>
          </ul>
          <button
            onClick={selectPlanHandler(monthlyPlan)}
            className={buttons.buttomPrimary}
            type="button"
          >
            {getSubscribeButtonText(isMonthlyPlanActive, 'Month to Month')}
          </button>
        </div>
        <div className={`${styles.plan} ${styles.mainBlock}`}>
          <div className={styles.chip}>Save 28% Annually!</div>
          <Title>Annual Plan</Title>
          <span>$5 per Month</span>
          <p>($60 billed annually)</p>
          <ul>
            <li>Up to 5 scholar profiles</li>
            <li>Unlimited Access to Aneta Map themes and Anetapack library</li>
            <li>Change or cancel your plan anytime!</li>
            <li>Peace of mind for about 25 cents a day!</li>
          </ul>
          <button
            onClick={selectPlanHandler(yearlyPlan)}
            className={buttons.buttomPrimary}
            type="button"
          >
            {getSubscribeButtonText(isYearlyPlanActive, 'Annually')}
          </button>
        </div>
      </div>
      <div className={styles.bottomActions}>
        {visible && (
          <div className={styles.informToolTip}>
            A monthly subscription is a period of 30 days, and an annual
            subscription is a period of 12* 30days, following the subscription
            date. <br />
            <br />
            For your convenience, plans are set to auto-renew by default but you
            will be notified prior to your subscription expiring and will have
            the option to change the autorenewal settings. <br /> <br />
            Under both plans, cancelled subscriptions will end after the current
            30 day period concludes. <br />
            <br />
            For annual plan cancellations Refunds are prorated at $5 per month
            per the remaining 30 day periods. <br />
            <br />
            No refunds are issued for month to month plans.
          </div>
        )}
        <span
          role="button"
          tabIndex={0}
          onKeyDown={() => null}
          onMouseEnter={() => setVisible(true)}
          onMouseLeave={() => setVisible(false)}
        >
          Additional Plan and Cancellation details
        </span>
        {isSubscriptionCancelable(subscriptionStatus) ? (
          <button
            type="button"
            className={styles.cancelSubscription}
            onClick={openCancelPage}
          >
            Cancel Subscription
          </button>
        ) : null}
      </div>
    </>
  );
};

export default BillingPlans;
